import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { useRecipeIdsByCoreIds } from 'hooks/menu-recipes/useRecipeIdsByCoreIds'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useStaticArray } from 'hooks/useStaticArray'
import type { Recipe, RecipeCoreId } from 'reducers/menu-recipes/recipes'
import { getBasketRecipes } from 'selectors/basket'

export type BasketRecipe = {
  coreId: RecipeCoreId
  recipe: Recipe | null // null means recipe is not loaded
  quantity: number
}

/**
 * Returns an array of basket recipes with their core IDs and the corresponding recipe objects.
 */
export const useBasketRecipes = () => {
  const basketQuantities = useSelector(getBasketRecipes)
  const basketCoreIds = useMemo(() => Array.from(basketQuantities.keys()), [basketQuantities])
  const basketUUIDs = useRecipeIdsByCoreIds(basketCoreIds)
  const recipes = useRecipes(basketUUIDs)
  const staticRecipes = useStaticArray(recipes)

  const basketRecipes = useMemo(
    () =>
      basketCoreIds.map((coreId, index) => ({
        coreId,
        recipe: staticRecipes[index] || null,
        quantity: basketQuantities.get(coreId) ?? 0,
      })),
    [basketCoreIds, staticRecipes, basketQuantities],
  )

  const basketCoreIdsSet = new Set(
    basketRecipes.filter((recipe) => recipe.quantity > 0).map((recipe) => recipe.coreId),
  )

  const basketHasRecipe = (coreId: RecipeCoreId) => basketCoreIdsSet.has(coreId)

  return {
    basketRecipes,
    basketHasRecipe,
  }
}
